import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { RevenueChartData } from '../../types/dashboard';
import { format, parseISO } from 'date-fns';

interface Props {
  data: RevenueChartData[];
  loading?: boolean;
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= 1000) {
    return (value / 1000).toFixed(0) + 'K';
  }
  return value.toString();
};

const formatTooltipValue = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value) + 'đ';
};

export const RevenueChart: React.FC<Props> = ({ data, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="card p-4">
        <div className="h-6 w-40 bg-gray-200 rounded mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="card p-4">
        <h3 className="text-lg font-semibold mb-4">{t('dashboard.revenueChart')}</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          {t('common.noData')}
        </div>
      </div>
    );
  }

  // Format data for chart
  const chartData = data.map((item) => ({
    ...item,
    dateLabel: format(parseISO(item.date), 'dd/MM'),
  }));

  return (
    <div className="card p-4">
      <h3 className="text-lg font-semibold mb-4">{t('dashboard.revenueChart')}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <Tooltip
            formatter={(value: number, name: string) => [
              formatTooltipValue(value),
              name === 'revenue'
                ? t('dashboard.revenue')
                : name === 'expenses'
                  ? t('dashboard.expenses')
                  : t('dashboard.profit'),
            ]}
            labelFormatter={(label) => `${t('common.date')}: ${label}`}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
          />
          <Legend
            formatter={(value) =>
              value === 'revenue'
                ? t('dashboard.revenue')
                : value === 'expenses'
                  ? t('dashboard.expenses')
                  : t('dashboard.profit')
            }
          />
          <Line
            type="monotone"
            dataKey="revenue"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: '#3b82f6', strokeWidth: 2 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="expenses"
            stroke="#f97316"
            strokeWidth={2}
            dot={{ fill: '#f97316', strokeWidth: 2 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="profit"
            stroke="#22c55e"
            strokeWidth={2}
            dot={{ fill: '#22c55e', strokeWidth: 2 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
